// TanStack Query hooks for the platform-admin panel (ADR-0024).
//
// Key prefix is ["admin", ...] so admin cache invalidations don't
// touch the facility-dashboard cache and vice versa. Per-query
// staleTime tuned per-surface — orgs list changes rarely, drafts and
// recipes are read-mostly.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  AdminBrandCreateRequest,
  AdminOrgCreateRequest,
  AdminOrgDefaultBrandUpdateRequest,
  AdminOrgDeleteRequest,
  AdminOrgInvitationCreateRequest,
  AdminOrgPatchRequest,
  AdminWorkbookCommitRequest,
  SupportAccessMintRequest,
} from "@bitacorapro/api-types";
import { ApiError } from "../../lib/api/error";
import { queryKeys } from "../../lib/api/queries";
import { adminApi, type WorkbookPreviewArgs } from "../../lib/api/endpoints/admin";
import { supportApi } from "../../lib/api/endpoints/support";

export const adminQueryKeys = {
  recipes: ["admin", "recipes"] as const,
  orgs: ["admin", "orgs"] as const,
  org: (id: string) => ["admin", "org", id] as const,
  orgMembers: (id: string) => ["admin", "org", id, "members"] as const,
  orgAssets: (id: string) => ["admin", "org", id, "assets"] as const,
  orgReports: (id: string) => ["admin", "org", id, "reports"] as const,
} as const;

export function useAdminRecipes() {
  return useQuery({
    queryKey: adminQueryKeys.recipes,
    queryFn: () => adminApi.listRecipes(),
    // Recipes are registry-driven and change only on deploy — long
    // staleTime is fine; the cache invalidates on full page reload.
    staleTime: 10 * 60 * 1000,
  });
}

export function useAdminOrgs() {
  return useQuery({
    queryKey: adminQueryKeys.orgs,
    queryFn: () => adminApi.listOrgs(),
    staleTime: 60 * 1000,
  });
}

export function useAdminOrg(id: string) {
  return useQuery({
    queryKey: adminQueryKeys.org(id),
    queryFn: () => adminApi.getOrg(id),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
}

// #0487 / core-api #0491 — GET /admin/orgs/:id/reports is live. A 404
// here means a genuine org-not-found (stale link, org deleted
// mid-session) — a terminal condition, not "route doesn't exist yet."
// No 4xx (400/403/404) is worth retrying since none resolve by
// retrying; 5xx and network failures (ApiError.status === 0) keep the
// default 3 attempts.
export function useAdminOrgReports(orgId: string) {
  return useQuery({
    queryKey: adminQueryKeys.orgReports(orgId),
    queryFn: () => adminApi.listOrgReports(orgId),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 3,
  });
}

// No cache invalidation on success — a mint doesn't change any list this
// org's other queries read. The response (a one-time bootstrap code) is
// single-use; it's never written into the QueryCache, so it can't be
// replayed from a stale cache entry on re-render or back-navigation. It
// does sit in the MutationCache (default 5-min gcTime) until the caller
// calls mint.reset() — the code itself is spent server-side well before
// that TTL elapses, so this is not a live-credential leak, just a reason
// not to treat "never cached" as an absolute claim.
export function useMintSupportGrant() {
  return useMutation({
    mutationFn: (body: SupportAccessMintRequest) => supportApi.mintGrant(body),
  });
}

export function useCreateAdminOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminOrgCreateRequest) => adminApi.createOrg(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgs });
    },
  });
}

// PATCH /admin/orgs/:id — soft toggle active. Invalidates both the
// detail and the list so the row badge updates everywhere.
export function usePatchAdminOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminOrgPatchRequest) => adminApi.patchOrg(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.org(orgId) });
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgs });
    },
  });
}

// DELETE /admin/orgs/:id — hard cascade. On success the org no longer
// exists; the caller (AdminOrgDetail) navigates back to AdminOrgs. We
// also pre-emptively remove the detail query from cache so a stale
// re-fetch doesn't 404.
export function useDeleteAdminOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminOrgDeleteRequest) => adminApi.deleteOrg(orgId, body),
    onSuccess: () => {
      qc.removeQueries({ queryKey: adminQueryKeys.org(orgId) });
      qc.removeQueries({ queryKey: adminQueryKeys.orgMembers(orgId) });
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgs });
    },
  });
}

export function usePreviewWorkbook() {
  return useMutation({
    mutationFn: (args: WorkbookPreviewArgs) => adminApi.previewWorkbook(args),
  });
}

export function useCommitWorkbook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminWorkbookCommitRequest) => adminApi.commitWorkbook(body),
    onSuccess: () => {
      // The commit creates a new org — bust the orgs list so the
      // panel reflects it on the next render.
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgs });
    },
  });
}

// Members live in auth-svc per ADR-0033 §Decision 3. Two parallel
// queries: this one + useAdminOrg(id) at the call site.
export function useAdminOrgMembers(id: string) {
  return useQuery({
    queryKey: adminQueryKeys.orgMembers(id),
    queryFn: () => adminApi.getOrgMembers(id),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
}

// Brand-logo commit invalidates the org-detail cache so the new
// logoUrl shows up immediately on the brand row. The upload-url
// mutation does NOT invalidate — the URL it returns is single-use
// and ephemeral; caching it would be wrong.
export function useCommitBrandLogo(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `kind` picks the config slot: "square" (default, padded isotipo)
    // or "wide" (horizontal owner-header logo, canal único fase 1).
    mutationFn: (args: { brandId: string; objectKey: string; kind?: "square" | "wide" }) =>
      adminApi.commitBrandLogo(orgId, args.brandId, {
        objectKey: args.objectKey,
        ...(args.kind ? { kind: args.kind } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.org(orgId) });
    },
  });
}

// After an org or brand logo lands (features/admin/logoUpload.ts calls
// adminApi directly, outside any mutation hook): bust the admin detail so
// the preview shows the new image, and GET /organizations/me so the
// sidebar of an operator who belongs to this org picks it up too.
export async function invalidateAfterLogo(qc: QueryClient, orgId: string): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: adminQueryKeys.org(orgId) }),
    qc.invalidateQueries({ queryKey: queryKeys.myOrganization }),
  ]);
}

// PUT /admin/orgs/:id/default-brand (ADR-0164). The default brand feeds
// the sidebar's last logo fallback, so /organizations/me goes stale too.
export function useSetDefaultBrand(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminOrgDefaultBrandUpdateRequest) => adminApi.setDefaultBrand(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.org(orgId) });
      void qc.invalidateQueries({ queryKey: queryKeys.myOrganization });
    },
  });
}

// Create a brand row in an existing org. The new brand has no logo;
// the operator can upload one via the existing /logo/upload-url flow
// on the row that this mutation surfaces. Invalidates the org-detail
// cache so the new brand appears in the panel.
export function useCreateBrand(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminBrandCreateRequest) => adminApi.createBrand(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.org(orgId) });
    },
  });
}

// POST /admin/orgs/:id/invitations/retry-failed — re-fan invitations
// the writer should have sent but auth-svc didn't accept. Returns the
// per-email result. Invalidates the members list so any new pending
// invites surface immediately.
export function useRetryFailedInvitations(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminApi.retryFailedInvitations(orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgMembers(orgId) });
    },
  });
}

// Flat list of an org's Tiendas, for the supervisor multi-store picker
// (ADR-0047). Gated `enabled` so it only fetches when the form needs it
// (role === facility_supervisor); asset lists change rarely mid-session.
export function useAdminOrgAssets(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.orgAssets(orgId),
    queryFn: () => adminApi.listOrgAssets(orgId),
    enabled: enabled && Boolean(orgId),
    staleTime: 5 * 60 * 1000,
  });
}

// Send an invitation. Routed through core-api's /admin/orgs/:id/invitations
// (ADR-0047) — not auth-svc directly — so a facility_supervisor's selected
// stores get stamped before the invitation fans out. Invalidates the
// members list, and the org-assets list so the picker reflects the new
// supervisor coverage on the next open.
export function useCreateInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminOrgInvitationCreateRequest) =>
      adminApi.createOrgInvitation(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgMembers(orgId) });
      void qc.invalidateQueries({ queryKey: adminQueryKeys.orgAssets(orgId) });
    },
  });
}
